import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { formatInAlbania } from '@/lib/date-utils';

interface AttendanceChartProps {
  selectedGym: string;
  className?: string;
}

const chartConfig = {
  checkIns: {
    label: 'Check-ins',
    color: 'hsl(var(--primary))',
  },
} satisfies ChartConfig;

export function AttendanceChart({ selectedGym, className }: AttendanceChartProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym,
  });

  const chartData = useMemo(() => {
    const buckets = Array.from({ length: 24 }, () => 0);
    (data?.attandanceCount ?? []).forEach((c) => {
      if (!c.checkIn) return;
      const hour = parseInt(formatInAlbania(c.checkIn, 'HH'), 10);
      if (!Number.isNaN(hour) && hour >= 0 && hour < 24) buckets[hour] += 1;
    });

    // Trim to the active window (first..last hour with data), lightly padded
    let first = buckets.findIndex((v) => v > 0);
    let last = buckets.length - 1 - [...buckets].reverse().findIndex((v) => v > 0);
    if (first === -1) {
      first = 6;
      last = 22;
    } else {
      first = Math.max(0, first - 1);
      last = Math.min(23, last + 1);
    }

    return Array.from({ length: last - first + 1 }, (_, i) => {
      const hour = first + i;
      return {
        hour: `${hour.toString().padStart(2, '0')}:00`,
        checkIns: buckets[hour],
      };
    });
  }, [data]);

  const totalCheckIns = data?.attandanceCount?.length ?? 0;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{t('dashboard.checkInsByHour', 'Check-ins by hour')}</CardTitle>
        <CardDescription>
          {t(
            'dashboard.checkInsByHourDesc',
            'Distribution of member check-ins across the day'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[300px] w-full animate-pulse rounded-lg bg-muted" />
        ) : totalCheckIns === 0 ? (
          <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
            {t('checkIn.noCheckIns')}
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="aspect-auto h-[300px] w-full">
            <AreaChart data={chartData} margin={{ left: 4, right: 12, top: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="fillCheckIns" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-checkIns)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-checkIns)" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={16}
              />
              <YAxis tickLine={false} axisLine={false} width={36} allowDecimals={false} tickMargin={8} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
              <Area
                dataKey="checkIns"
                type="monotone"
                stroke="var(--color-checkIns)"
                strokeWidth={2}
                fill="url(#fillCheckIns)"
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
